import { useTranslation } from '@exam-room/translations';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Button,
  Card,
  CancelOutlinedIcon,
  CheckCircleOutlinedIcon,
  Page,
  UploadFileIcon,
  CustomWarningOutlineIcon,
} from '../../components';
import { useAuth } from '../../providers';
import { PATHS } from '../constants';

interface ExamineeInfo {
  isDisplayResult: boolean;
  isPassed: boolean | null;
  hasCertificate: boolean;
  title: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
}

interface ExamResultVariant {
  icon: React.ReactNode;
  heading: string;
  subtext?: string;
  showNameBox: boolean;
}

function getVariant(examineeInfo: ExamineeInfo, t: (key: string) => string): ExamResultVariant {
  const { isDisplayResult, isPassed, hasCertificate } = examineeInfo;

  if (isDisplayResult && isPassed === true) {
    return {
      icon: (
        <span className='bg-success-100 grid h-50 w-50 place-items-center rounded-[50%]'>
          <CheckCircleOutlinedIcon className='text-success-500 text-[100px]' />
        </span>
      ),
      heading: t('examResult.relogin.passed'),
      showNameBox: true,
    };
  }

  if (isDisplayResult && isPassed === false) {
    return {
      icon: (
        <span className='bg-danger-100 grid h-50 w-50 place-items-center rounded-[50%]'>
          <CancelOutlinedIcon className='text-danger-500 text-[120px]' />
        </span>
      ),
      heading: t('examResult.relogin.failed'),
      showNameBox: true,
    };
  }

  if (isDisplayResult && isPassed === null) {
    return {
      icon: (
        <span className='bg-warning-100 grid h-50 w-50 place-items-center rounded-[50%]'>
          <CustomWarningOutlineIcon className='text-warning-500 text-[100px]' />
        </span>
      ),
      heading: t('examResult.relogin.resultUnavailable'),
      subtext: t('examResult.relogin.contactProctor'),
      showNameBox: false,
    };
  }

  // !isDisplayResult
  return {
    icon: (
      <span className='bg-info-100 grid h-50 w-50 place-items-center rounded-[50%]'>
        <UploadFileIcon className='text-info text-[100px]' />
      </span>
    ),
    heading: t('examResult.relogin.submitted'),
    subtext: hasCertificate
      ? t('examResult.relogin.emailNotification')
      : t('examResult.relogin.waitForResult'),
    showNameBox: true,
  };
}

function ExamResult() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const examineeInfo = (location.state as { examineeInfo?: ExamineeInfo } | null)?.examineeInfo;
  const handleLogout = async () => {
    await logout();
    navigate(PATHS.Home);
  };

  useEffect(() => {
    if (!examineeInfo) {
      navigate(PATHS.Login);
    }
  }, [examineeInfo, navigate]);

  if (!examineeInfo) {
    return null;
  }

  const variant = getVariant(examineeInfo, t);
  const fullName = [
    examineeInfo.title,
    examineeInfo.firstName,
    examineeInfo.middleName,
    examineeInfo.lastName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className='bg-grey-50 grid size-full place-items-center p-6'>
      <Card className='flex w-[800px] flex-col items-center gap-6 p-10'>
        {variant.icon}

        <div className='flex w-[420px] flex-col items-center gap-3 text-center'>
          <h3>{variant.heading}</h3>
          {variant.subtext ? (
            <p className='text-body1 text-text-secondary'>{variant.subtext}</p>
          ) : null}
        </div>

        {variant.showNameBox ? (
          <div className='w-[420px] rounded-md border border-gray-300 p-4 text-center'>
            <h4>{fullName}</h4>
          </div>
        ) : null}

        <Button className='w-[420px]' onClick={handleLogout}>
          {t('logout')}
        </Button>
      </Card>
    </div>
  );
}

const ExamResultRoute = {
  path: PATHS.ExamResult,
  element: (
    <Page>
      <ExamResult />
    </Page>
  ),
};

export default ExamResultRoute;
